import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  RICERCA_DOCUMENTI,
  type RigaDocumento,
  esportaDocumenti,
  indirizzoScarico,
  useConteggiCategorie,
  useDefinizioneFiltriDocumenti,
  useDocumenti,
} from '../api/documenti.js';
import { AzioniDiMassa } from '../componenti/AzioniDiMassa.js';
import { BarraFiltri } from '../componenti/BarraFiltri.js';
import { PiedeElenco } from '../componenti/PiedeElenco.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { data, numero } from '../formati.js';
import { useRicerca } from '../useRicerca.js';
import { useSelezione } from '../useSelezione.js';
import { ModaleDocumentiMultipli } from './ModaleDocumentiMultipli.js';
import { ModaleDocumento } from './ModaleDocumento.js';
import { locale, t } from '../i18n/lingua.js';
import { SeApre } from '../componenti/SeApre.js';

/**
 * L'archivio documentale: ottantaduemila documenti.
 *
 * Nel v2 se ne vedono cento per volta e per trovare qualcosa si compila una
 * maschera con undici campi. Qui si scrive una cosa sola nella casella —
 * numero d'ordine, titolo, cliente, protocollo, nome del file — e la riga di
 * pastiglie dice quanti POD, quante lettere di valorizzazione, quanti PO.
 *
 * La colonna del nome file mostra **il nome con cui il file esce davvero**:
 * per 9.532 documenti quello scritto a database è `untitled.pdf` o
 * `conferma-di-consegna_7.pdf`, e non serve a ritrovare niente.
 */

/** Quante pastiglie di categoria stanno in una riga senza mandarla a capo. */
const PASTIGLIE_VISIBILI = 7;

const COLONNE: Colonna<RigaDocumento>[] = [
  {
    chiave: 'title',
    titolo: t('Titolo'),
    ordinabile: true,
    larghezzaMax: 420,
    cella: (r) => (
      <>
        <Link className="cell-link strong" to="/documents/$id" params={{ id: String(r.id) }}>
          {r.title}
        </Link>
        {r.archived && (
          <span className="badge" style={{ marginLeft: 8 }}>
            {t('archiviato')}
          </span>
        )}
      </>
    ),
  },
  {
    chiave: 'createdAt',
    titolo: t('Data'),
    ordinabile: true,
    monospazio: true,
    cella: (r) => data(r.createdAt),
  },
  {
    chiave: 'categories',
    titolo: t('Categorie'),
    cella: (r) =>
      r.categories.length === 0 ? (
        <span className="dim">—</span>
      ) : (
        <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {r.categories.map((c) => (
            <span className="badge" key={c}>
              {c}
            </span>
          ))}
        </span>
      ),
  },
  {
    chiave: 'customer',
    titolo: t('Cliente'),
    ordinabile: true,
    larghezzaMax: 240,
    cella: (r) =>
      r.identityId ? (
        <SeApre sezione="customers" altrimenti={<span>{r.customer}</span>}>
          <Link className="cell-link" to="/customers/$id" params={{ id: String(r.identityId) }}>
            {r.customer}
          </Link>
        </SeApre>
      ) : (
        <span className="dim">—</span>
      ),
  },
  {
    chiave: 'orderNumber',
    titolo: t('Ordine'),
    ordinabile: true,
    monospazio: true,
    cella: (r) =>
      r.orderId ? (
        <SeApre sezione="sales-orders" altrimenti={<span>{r.orderNumber}</span>}>
          <Link className="cell-link" to="/sales-orders/$id" params={{ id: String(r.orderId) }}>
            {r.orderNumber}
          </Link>
        </SeApre>
      ) : (
        <span className="dim">—</span>
      ),
  },
  {
    chiave: 'downloadName',
    titolo: t('File'),
    larghezzaMax: 300,
    cella: (r) =>
      r.fileMissing ? (
        // registrato ma sparito dall'archivio: un link portava a un errore
        <span className="badge badge-warn" title={r.downloadName}>
          {t('file non in archivio')}
        </span>
      ) : r.fileName ? (
        // scarica col nome vero: chi ne prende venti non si ritrova venti
        // `untitled.pdf` nella cartella dei download
        <a className="cell-link" href={indirizzoScarico(r.id)} download={r.downloadName}>
          {r.downloadName}
        </a>
      ) : (
        <span className="badge badge-warn">{t('senza file')}</span>
      ),
  },
];

export function Documenti() {
  const { ricerca, testo, setTesto, incolla, cambia, vaiA, ordinaPer, filtra } =
    useRicerca(RICERCA_DOCUMENTI);
  const elenco = useDocumenti(ricerca);
  const [selezione, setSelezione] = useSelezione(elenco.data?.rows);
  const conteggi = useConteggiCategorie(ricerca);
  const definizioni = useDefinizioneFiltriDocumenti();
  const [nuovoAperto, setNuovoAperto] = useState(false);
  const [multipliAperti, setMultipliAperti] = useState(false);
  const [tutteLeCategorie, setTutteLeCategorie] = useState(false);

  const dati = elenco.data;
  const scelta = ricerca.filters?.categoryId;
  const categorie = conteggi.data?.counts ?? [];
  const mostrate = tutteLeCategorie ? categorie : categorie.slice(0, PASTIGLIE_VISIBILI);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Documenti')}</h1>
          <div className="page-sub">
            {dati
              ? ricerca.archived
                ? t('{n} documenti, archiviati compresi', { n: dati.total.toLocaleString(locale()) })
                : t('{n} documenti', { n: dati.total.toLocaleString(locale()) })
              : elenco.error
                ? t('Elenco non disponibile')
                : t('Un momento…')}
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaDocumenti(ricerca, 'csv')}>
            CSV
          </button>
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaDocumenti(ricerca, 'xlsx')}>
            Excel
          </button>
          <button className="btn" onClick={() => setMultipliAperti(true)}>
            {t('Un file, più ordini')}
          </button>
          <button className="btn btn-primary" onClick={() => setNuovoAperto(true)}>
            {t('Nuovo documento')}
          </button>
        </div>
      </div>

      <div className="statebar">
        <button
          className={scelta === undefined ? 'state-tab state-tab-on' : 'state-tab'}
          onClick={() => filtra('categoryId', undefined)}
        >
          {t('Tutti')}
          {/* tutti i documenti della ricerca, di qualunque categoria: il totale
              dell'elenco, con una categoria scelta, era quello della categoria */}
          <b>{(conteggi.data?.total ?? dati?.total ?? 0).toLocaleString('it-IT')}</b>
        </button>
        {mostrate.map((c) => (
          <button
            key={c.id}
            className={String(scelta) === String(c.id) ? 'state-tab state-tab-on' : 'state-tab'}
            onClick={() => filtra('categoryId', String(scelta) === String(c.id) ? undefined : c.id)}
          >
            {c.name}
            <b>{c.count.toLocaleString('it-IT')}</b>
          </button>
        ))}
        {categorie.length > PASTIGLIE_VISIBILI && (
          <button className="state-tab" onClick={() => setTutteLeCategorie((v) => !v)}>
            {tutteLeCategorie ? 'meno' : `altre ${categorie.length - PASTIGLIE_VISIBILI}`}
          </button>
        )}
      </div>

      <div className="filterbar">
        <span className="omnisearch" style={{ maxWidth: 420, flex: '0 1 420px' }}>
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per titolo, PO#, cliente, protocollo, nome del file')}
            title={t('Più PO# insieme: incollali separati da virgole, spazi o a capo')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            onPaste={incolla}
          />
        </span>

        <button
          className={ricerca.archived ? 'chip chip-set' : 'chip'}
          onClick={() => cambia({ archived: !ricerca.archived })}
        >
          {ricerca.archived ? t('Con gli archiviati') : t('Mostra archiviati')}
        </button>

        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      {/* come negli Ordini; qui il PO che manca è un ordine senza documenti,
          e con una categoria scelta è quello a cui manca ancora il POD */}
      {dati?.poList && (
        <div className="avviso-riga" role="status" style={{ marginTop: 0 }}>
          <span>
            {dati.poList.notFound.length === 0
              ? t('{n} PO# cercati: tutti hanno documenti.', { n: numero(dati.poList.searched) })
              : t('{n} PO# cercati. Senza documenti: {elenco}', {
                  n: numero(dati.poList.searched),
                  elenco: dati.poList.notFound.join(', '),
                })}
          </span>
        </div>
      )}

      <AzioniDiMassa
        percorso="/api/documents/bulk"
        nome="documenti"
        selezione={selezione}
        conElimina
        conRipristina={ricerca.archived}
        chiaviDaRileggere={['documenti', 'documenti-conteggi']}
        onFatto={() => setSelezione(new Set())}
        onAnnulla={() => setSelezione(new Set())}
      />

      <div className="table-wrap" style={{ marginTop: 12, marginBottom: 24 }}>
        <Tabella
          colonne={COLONNE}
          righe={dati?.rows ?? []}
          chiaveRiga={(r) => r.id}
          ordina={ricerca.sort}
          verso={ricerca.dir}
          onOrdina={ordinaPer}
          selezione={selezione}
          onSelezione={setSelezione}
          caricamento={elenco.isFetching}
          errore={elenco.error}
          vuoto={
            ricerca.q ? t('Nessun documento per «{cerca}».', { cerca: ricerca.q }) : t('Nessun documento con questi filtri.')
          }
        />
        <PiedeElenco
          pagina={dati?.page ?? 1}
          pagine={dati?.pages ?? 1}
          totale={dati?.total ?? 0}
          dimensione={ricerca.size}
          nomePlurale={t('documenti')}
          onPagina={vaiA}
          onDimensione={(size) => cambia({ size })}
        />
      </div>

      {nuovoAperto && <ModaleDocumento documento={null} onChiudi={() => setNuovoAperto(false)} />}
      {multipliAperti && <ModaleDocumentiMultipli onChiudi={() => setMultipliAperti(false)} />}
    </>
  );
}
